import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Phone, Mail, Hash, Globe, Grab } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function KanbanCard({ lead }) {
  const navigate = useNavigate();
  const isValidLead = Boolean(lead && lead.id);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging
  } = useDraggable({
    id: isValidLead ? lead.id : 'invalid-lead',
    disabled: !isValidLead,
    data: isValidLead ? { type: 'lead', lead } : undefined
  });

  if (!isValidLead) return null;

  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 100 : undefined,
    visibility: isDragging ? 'hidden' : 'visible'
  };

  const origenStyles = (origen) => {
    switch (origen?.toLowerCase()) {
      case 'paid':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'orgánico':
      case 'organico':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'referencia':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => navigate(`/leads/${lead.id}`)}
      className={`
        bg-white rounded-xl px-3 py-2.5 border border-slate-100
        cursor-pointer transition-all
        hover:shadow-sm hover:border-blue-300 group relative
        ${isDragging ? 'opacity-0' : 'opacity-100 shadow-sm'}
      `}
    >
      {/* DRAG HANDLE (SOLO AQUÍ SE ARRASTRA) */}
      <div
        {...listeners}
        {...attributes}
        className="absolute top-2 right-2 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Grab size={12} className="text-slate-300" />
      </div>

      {/* NOMBRE + FUENTE */}
      <div className="flex items-start justify-between gap-2 mb-2 pr-6">
        <h3 className="font-bold text-slate-800 text-xs leading-none truncate">
          {lead.nombre || 'Sin nombre'}
        </h3>

        {lead.utm_source && (
          <span className="text-[9px] font-semibold uppercase tracking-tight text-slate-400">
            {lead.utm_source.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {/* CONTACTO */}
      <div className="flex flex-col gap-1">
        {lead.telefono && (
          <div className="flex items-center gap-1.5 text-slate-500">
            <Phone size={10} />
            <span className="text-[10px] font-medium">
              {lead.telefono}
            </span>
          </div>
        )}
        {lead.email && (
          <div className="flex items-center gap-1.5 text-slate-500">
            <Mail size={10} />
            <span className="text-[10px] truncate max-w-[160px]">
              {lead.email}
            </span>
          </div>
        )}
      </div>

      {/* ORIGEN + ID */}
      <div className="mt-2.5 pt-2 border-t border-slate-50 flex items-center justify-between">
        <div
          className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[8px] font-black uppercase ${origenStyles(
            lead.origen_nombre
          )}`}
        >
          <Globe size={9} />
          <span>{lead.origen_nombre || 'Directo'}</span>
        </div>

        <div className="flex items-center gap-0.5 text-slate-300">
          <Hash size={9} />
          <span className="text-[8px] font-bold">
            {lead.id.substring(0, 4)}
          </span>
        </div>
      </div>
    </div>
  );
}
